"use client";

import { appConfig } from "@/constants";
import {
  Action,
  ControlType,
  useAnalytics,
} from "@/context/AnalyticsProvider/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import {
  Body,
  Box,
  ConfirmationDialog,
  Heading,
  MenuItem,
  Stack,
} from "@biom3/react";
import { GAMES } from "@components/Onboarding/constants";
import Link from "next/link";
import { useCallback, useEffect } from "react";

export type CrossGameModalProps = {
  open: boolean;
  onClose: () => void;
};

export default function CrossGameModal({ open, onClose }: CrossGameModalProps) {
  const { trackV3 } = useAnalytics();
  const { isLoggedIn } = usePassportProvider();

  const trackLink = useCallback(
    (label: string, url: string, gameId?: string, gameName?: string) => {
      trackV3({
        object: "Cross_Game_Modal_Link",
        action: Action.PRESSED,
        extras: {
          // Link-specific properties
          id: `crossGameModal-${label.replace(/\s+/g, "").toLowerCase()}`,
          type: "gameLink",
          label,
          section: "CrossGameModal",
          url,
          // Common
          isLoggedIn: Boolean(isLoggedIn),
          // Control semantics
          control: "GameLink",
          controlLabel: label,
          controlType: ControlType.LINK,
          // Game context
          gameId: gameId ?? url.split("/").pop(),
          gameName: gameName ?? label,
        },
      });
    },
    [trackV3, isLoggedIn],
  );

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <ConfirmationDialog
      testId="cross-game-modal"
      visible={open}
      outsideClicksClose
      escapeKeyClose
      onCloseModal={onClose}
    >
      <ConfirmationDialog.Content
        textAlign="left"
        sx={{
          maxWidth: { default: "100dvw", small: "430px", medium: "500px" },
          backgroundColor: "base.color.neutral.800",
        }}
      >
        <ConfirmationDialog.Content.CloseButtCon
          testId="cross-game-modal__close"
          onClick={onClose}
        />

        <ConfirmationDialog.Content.Image
          testId="cross-game-modal__image"
          imageUrl={`${appConfig.ASSET_BASE_URL}${"/assets/banner/nintendo-rule.webp"}`}
          aspectRatio={4}
          responsiveSizes={[370, 450, 512, 720, 1024]}
          sx={{ height: "161px" }}
        />

        <ConfirmationDialog.Content.Title
          testId="cross-game-modal__title"
          size="small"
          sx={{ mb: "base.spacing.x4" }}
        >
          <Heading size="medium">
            The biggest month of Quests on Immutable. Win a Nintendo Switch 2.
          </Heading>
        </ConfirmationDialog.Content.Title>

        <ConfirmationDialog.Content.Caption
          testId="cross-game-modal__caption"
          sx={{ pt: "0px" }}
        >
          <Body size="small" sx={{ color: "base.color.text.body.primary" }}>
            Prove your skills in your favourite games and get a chance to win a
            Nintendo Switch 2! Complete all of the quests that become available
            over the coming weeks in our featured games — Might & Magic Fates,
            Triumph Games, and Meta Toy Dragonz Saga — to earn a chance to win
            this exciting prize.
          </Body>
          <Box
            sx={{
              display: "grid",
              gap: "base.spacing.x4",
              pt: "base.spacing.x5",
            }}
          >
            <Body size="small" sx={{ pl: "base.spacing.x4" }}>
              <li>Complete all quests by 15 Sep 2025</li>
              <li>We'll contact the winner by email</li>
              <li>
                See the{" "}
                <Link
                  href="https://www.immutable.com/promotion-terms---nintendo-switch-2"
                  target="_blank"
                >
                  full terms and conditions
                </Link>{" "}
                for more details
              </li>
            </Body>
          </Box>
        </ConfirmationDialog.Content.Caption>
        <Stack>
          <Link
            href="/games/fates"
            onClick={() =>
              trackLink(
                "Might and Magic Fates",
                "/games/fates",
                "fates",
                "Might and Magic Fates",
              )
            }
          >
            <MenuItem emphasized size="xSmall">
              <MenuItem.FramedImage imageUrl="https://cdn.prod.website-files.com/6690d3b7585fd4942634f0fe/689ed3742d067a6287fed77a_fates.webp" />
              <MenuItem.Label>Might and Magic Fates</MenuItem.Label>
              <MenuItem.Caption>Ubisoft</MenuItem.Caption>
              <MenuItem.IntentIcon icon="ChevronForward" />
            </MenuItem>
          </Link>
          <Link
            href={GAMES.mtdz.ab}
            onClick={() =>
              trackLink(GAMES.mtdz.name, GAMES.mtdz.ab, "mtdz", GAMES.mtdz.name)
            }
          >
            <MenuItem emphasized size="xSmall">
              <MenuItem.FramedImage imageUrl="https://cdn.prod.website-files.com/6690d3b7585fd4942634f0fe/68806b8ec74cc7a6eda3c1d1_mtdz_icon.webp" />
              <MenuItem.Label>Meta Toy Dragonz Saga</MenuItem.Label>
              <MenuItem.Caption>Sandbox Network</MenuItem.Caption>
              <MenuItem.IntentIcon icon="ChevronForward" />
            </MenuItem>
          </Link>
          <Link
            href="/games/triumph"
            onClick={() =>
              trackLink(
                "Triumph Games",
                "/games/triumph",
                "triumph",
                "Triumph Games",
              )
            }
          >
            <MenuItem emphasized size="xSmall">
              <MenuItem.FramedImage imageUrl="https://cdn.prod.website-files.com/6690d3b7585fd4942634f0fe/687dc881af47bc39dbd2e5fe_Triumph%20Logo%20Expanded.webp" />
              <MenuItem.Label>Triumph Games</MenuItem.Label>
              <MenuItem.Caption>Triumph Games</MenuItem.Caption>
              <MenuItem.IntentIcon icon="ChevronForward" />
            </MenuItem>
          </Link>
        </Stack>
      </ConfirmationDialog.Content>
    </ConfirmationDialog>
  );
}
